import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Storage } from 'aws-amplify';

/**
 * Reader / patcher for the IZ/PR bank statement XML that thumbnailGenerator
 * writes to `public/bankStatements/{invoiceId}.xml` for every 5D statement.
 *
 *   <IZ RACUN="…" VALUTA="KM" POCETNI_SALDO="…" NA_TERET="…" U_KORIST="…"
 *       KONACNI_SALDO="…" DATUM_IZVODA="…" BROJ_IZVODA="…" BROJ_TRANSAKCIJA="…">
 *     <PR RED_BR="1" SIFRA_DOZNAKE="…" NALOGOPRIMAC_1="/account" NALOGOPRIMAC_2="name"
 *         NALOGOPRIMAC_3="." SVRHA="…" KNJIZENJE="D|C" IZNOS="1,00"
 *         DATUM_VALUTE="…" DATUM_NALOGA="…"/>
 *   </IZ>
 *
 * The same file is the source of the Opis_1 / Opis_2 / Svrha / Duguje /
 * Potrazuje columns in the Goods & Material preview and the file the user
 * downloads, enriched with the Konto / ID broj / Sifra partnera attributes.
 */
export interface BankStatementXmlHeader {
  racun: string;
  valuta: string;
  pocetniSaldo: string;
  naTeret: string;
  uKorist: string;
  konacniSaldo: string;
  datumIzvoda: string;
  brojIzvoda: string;
}

export interface BankStatementXmlRow {
  redBr: string;
  sifraDoznake: string;
  opis1: string;
  opis2: string;
  opis3: string;
  svrha: string;
  knjizenje: string;
  iznos: string;
  duguje: string;
  potrazuje: string;
  datumValute: string;
  datumNaloga: string;
}

export interface BankStatementXml {
  raw: string;
  header: BankStatementXmlHeader;
  rows: BankStatementXmlRow[];
}

/** Attributes to add to one <PR> element, keyed by RED_BR. */
export type BankStatementXmlPatch = { [redBr: string]: { [attr: string]: string } };

const XML_NAME_RE = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

/** Attributes thumbnailGenerator already writes on <PR>; they must never be overwritten. */
export const BANK_STATEMENT_PR_ATTRIBUTES = [
  'RED_BR', 'SIFRA_DOZNAKE', 'NALOGOPRIMAC_1', 'NALOGOPRIMAC_2', 'NALOGOPRIMAC_3',
  'SVRHA', 'KNJIZENJE', 'IZNOS', 'DATUM_VALUTE', 'DATUM_NALOGA',
];

/** NALOGOPRIMAC_1 is written as "/account" and empty NALOGOPRIMAC_3 as "."; show the bare values. */
function cleanNalogoprimac(value: string): string {
  const v = (value || '').trim();
  return v === '.' || v === '-' ? '' : v.replace(/^\/+/, '');
}

@Injectable({ providedIn: 'root' })
export class BankStatementXmlService {
  private cache: { [invoiceId: string]: BankStatementXml } = {};

  constructor(private http: HttpClient) {}

  static xmlKey(invoiceId: string): string {
    return `public/bankStatements/${invoiceId}.xml`;
  }

  static isValidAttributeName(name: string): boolean {
    return XML_NAME_RE.test(name || '') && !/^xml/i.test(name) && !this.isReservedAttributeName(name);
  }

  static isReservedAttributeName(name: string): boolean {
    return BANK_STATEMENT_PR_ATTRIBUTES.includes((name || '').toUpperCase());
  }

  async fetchRaw(invoiceId: string): Promise<string> {
    const signedUrl = await Storage.get(BankStatementXmlService.xmlKey(invoiceId), {
      customPrefix: { public: '' },
      level: 'public',
      download: false,
      expires: 300,
    });
    const text = await this.http
      .get(signedUrl as string, { responseType: 'text' })
      .toPromise();
    return text || '';
  }

  async load(invoiceId: string, force = false): Promise<BankStatementXml | null> {
    if (!force && this.cache[invoiceId]) return this.cache[invoiceId];
    try {
      const raw = await this.fetchRaw(invoiceId);
      const parsed = BankStatementXmlService.parse(raw);
      if (parsed) this.cache[invoiceId] = parsed;
      return parsed;
    } catch (err) {
      console.warn(`[BankStatementXml] Could not load XML for invoice ${invoiceId}:`, err);
      return null;
    }
  }

  static parse(raw: string): BankStatementXml | null {
    if (!raw || !raw.includes('<IZ')) return null;
    const doc = new DOMParser().parseFromString(raw, 'application/xml');
    const iz = doc.getElementsByTagName('IZ')[0];
    if (!iz) return null;
    const attr = (el: Element, name: string) => el.getAttribute(name) || '';

    const header: BankStatementXmlHeader = {
      racun: attr(iz, 'RACUN'),
      valuta: attr(iz, 'VALUTA'),
      pocetniSaldo: attr(iz, 'POCETNI_SALDO'),
      naTeret: attr(iz, 'NA_TERET'),
      uKorist: attr(iz, 'U_KORIST'),
      konacniSaldo: attr(iz, 'KONACNI_SALDO'),
      datumIzvoda: attr(iz, 'DATUM_IZVODA'),
      brojIzvoda: attr(iz, 'BROJ_IZVODA'),
    };

    const rows: BankStatementXmlRow[] = Array.from(iz.getElementsByTagName('PR')).map((pr) => {
      const knjizenje = attr(pr, 'KNJIZENJE').toUpperCase();
      const iznos = attr(pr, 'IZNOS');
      return {
        redBr: attr(pr, 'RED_BR'),
        sifraDoznake: attr(pr, 'SIFRA_DOZNAKE'),
        opis1: cleanNalogoprimac(attr(pr, 'NALOGOPRIMAC_1')),
        opis2: cleanNalogoprimac(attr(pr, 'NALOGOPRIMAC_2')),
        opis3: cleanNalogoprimac(attr(pr, 'NALOGOPRIMAC_3')),
        svrha: attr(pr, 'SVRHA'),
        knjizenje,
        iznos,
        duguje: knjizenje === 'D' ? iznos : '',
        potrazuje: knjizenje === 'C' ? iznos : '',
        datumValute: attr(pr, 'DATUM_VALUTE'),
        datumNaloga: attr(pr, 'DATUM_NALOGA'),
      };
    });

    return { raw, header, rows };
  }

  /**
   * Add attributes to the <PR> elements of the original XML text. The file is
   * patched as text, so everything the importer already accepts (line endings,
   * attribute order, formatting) stays exactly as generated; only the new
   * attributes are inserted before the closing `/>`. Attributes with the same
   * name that are already present are replaced, so patching is idempotent.
   */
  static patch(raw: string, patches: BankStatementXmlPatch): string {
    return raw.replace(/<PR\b([^>]*?)(\/?)>/g, (full, attrs: string, selfClose: string) => {
      const redBr = /\bRED_BR="([^"]*)"/.exec(attrs)?.[1];
      const add = redBr !== undefined ? patches[redBr] : undefined;
      if (!add) return full;
      let body = attrs;
      const extra: string[] = [];
      for (const [name, value] of Object.entries(add)) {
        if (!BankStatementXmlService.isValidAttributeName(name)) continue;
        const escapedName = name.replace(/[.\-]/g, '\\$&');
        body = body.replace(new RegExp(`\\s+${escapedName}="[^"]*"`, 'g'), '');
        extra.push(`${name}="${BankStatementXmlService.escapeAttr(value)}"`);
      }
      if (!extra.length) return full;
      return `<PR${body.replace(/\s+$/, '')} ${extra.join(' ')}${selfClose}>`;
    });
  }

  static escapeAttr(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
